// uploadEnforcementAnalysis.tsx
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import FileUpload from "components/FileUpload";
import React, { useEffect, useState } from "react";
import { updateAuditLawImplementation } from "services/lawImplementationService";
import { LoadingIcon } from "utils/constant";
import { LEGAL_ADVISORY_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface UploadEnforcementAnalysisProps {
    data: any;
    setLegalResearch: Function;
    isOpen: boolean;
    onClose: () => void;
}

const UploadEnforcementAnalysis: React.FC<UploadEnforcementAnalysisProps> = ({
    data: singleData,
    setLegalResearch,
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    const contextData = useAuth();

    // compute the most recent enforcement analysis document (if any)
    const existingDoc = React.useMemo(() => {
        const docs = singleData?.documents || [];
        return docs
            .filter((d: any) => d.document_type === "enforcement_analysis_files")
            .slice()
            .reverse()?.[0];
    }, [singleData]);

    const [formState, setFormState] = useState({
        enforcement_files: null as File | null,
        enforcement_analysis_summary: singleData?.enforcement_analysis_summary || "",
    });

    // when the component opens or the data changes, prefill the summary
    useEffect(() => {
        if (singleData) {
            setFormState((prev) => ({
                ...prev,
                enforcement_analysis_summary: singleData.enforcement_analysis_summary || "",
            }));
        }
    }, [singleData]);

    const handleFileChange = (file: File | null) => {
        setFormState((prev) => ({
            ...prev,
            enforcement_files: file,
        }));
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    if (!isOpen) return null;

    const handleUploadSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        const data = new FormData();

        data.append("enforcement_files", formState?.enforcement_files ?? "");
        data.append("enforcement_analysis_summary", formState?.enforcement_analysis_summary ?? "");

        try {
            const response = await updateAuditLawImplementation(singleData.id, data);

            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("EnforcementAnalysisUploadedSuccess") || "Enforcement Analysis uploaded successfully!", "success");
        } catch (error) {
            console.error("Error uploading enforcement analysis:", error);
            showToast(t("FailedToUploadEnforcementAnalysis") || "Failed to upload enforcement analysis.", "error");
        } finally {
            setSubmitting(false);
        }

        onClose(); // Close modal after upload
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <form
                onSubmit={handleUploadSubmit}
                className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 relative"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("UploadEnforcementAnalysis") || "Upload Enforcement Analysis"}
                </h2>

                {/* Form Fields */}
                <div className="mb-4">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("EnforcementAnalysisFile") || "Enforcement Analysis File"}
                        </label>
                        {existingDoc ? (
                            <div className="mb-2">
                                <a
                                    href={`${process.env.REACT_APP_API_BASE_URL}law_implementation_follow_up/audit-follow-up-documents/document/${existingDoc.document_uuid}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline text-sm"
                                >
                                    {existingDoc.document_path.split("/").pop()}
                                </a>
                                <p className="text-xs text-gray-500">{t("CurrentFile") || "(current file)"}</p>
                            </div>
                        ) : null}
                        <FileUpload onChange={handleFileChange} />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("EnforcementAnalysisSummary") || "Enforcement Analysis Summary"}
                        </label>
                        <textarea
                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 resize-y min-h-[100px]"
                            placeholder={t("EnterEnforcementAnalysisSummaryPlaceholder") || "Enter enforcement analysis summary..."}
                            name="enforcement_analysis_summary"
                            value={formState.enforcement_analysis_summary}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* Submit Button */}
                <div className="text-center mt-6 flex gap-4">
                    <button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#212121] hover:bg-green-600 text-white font-bold py-3 px-8 rounded-md w-full disabled:opacity-70"
                    >
                        {submitting ? (
                            <div className="flex items-center justify-center">
                                {LoadingIcon} {t("Uploading") || "Uploading..."}
                            </div>
                        ) : (
                            t("Upload") || "Upload"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UploadEnforcementAnalysis;